import {
  BannerLevel,
  BannerScope,
  BannerSource,
  BannerLocalizedText,
} from './constants';

export const API_PATH = 'maintenance-banners';
export const AUDIT_RESOURCE = 'maintenance-banners';
export const PERMISSION_RESOURCE = 'maintenance-banners';

const SCOPE_TO_API: Record<BannerScope, string> = {
  global: 'GLOBAL',
  perps: 'PERPS',
  prediction: 'PREDICTION',
  fingertip: 'TAPGRID',
};

const SCOPE_FROM_API: Record<string, BannerScope> = {
  GLOBAL: 'global',
  PERPS: 'perps',
  PREDICTION: 'prediction',
  TAPGRID: 'fingertip',
};

const LEVEL_TO_API: Record<BannerLevel, string> = {
  maintenance: 'MAINTENANCE',
  notice: 'NOTICE',
  alert: 'ALERT',
};

const LEVEL_FROM_API: Record<string, BannerLevel> = {
  MAINTENANCE: 'maintenance',
  NOTICE: 'notice',
  ALERT: 'alert',
};

const SOURCE_TO_API: Record<BannerSource, string> = {
  manual: 'MANUAL',
  auto: 'AUTO',
};

const SOURCE_FROM_API: Record<string, BannerSource> = {
  MANUAL: 'manual',
  AUTO: 'auto',
};

export type MaintenanceBannerRecord = {
  id: string;
  code?: string;
  version?: number;
  scope: BannerScope;
  level: BannerLevel;
  source: BannerSource;
  message: BannerLocalizedText;
  linkText?: BannerLocalizedText;
  linkUrl?: string;
  dismissible?: boolean;
  startAt?: string | null;
  endAt?: string | null;
  venue?: string;
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  hasAuditLog?: boolean;
};

const normalizeLocalized = (value?: BannerLocalizedText | null): BannerLocalizedText | undefined => {
  if (!value) return {};
  const en = value.en?.trim() ?? '';
  const zh = value.zh?.trim() ?? '';
  return { en, zh };
};

export const fromApiRecord = (row: any): MaintenanceBannerRecord => ({
  id: String(row?.id ?? row?.code ?? ''),
  code: row?.code,
  version: row?.version,
  scope: SCOPE_FROM_API[row?.scope] ?? row?.scope,
  level: LEVEL_FROM_API[row?.level] ?? row?.level,
  source: SOURCE_FROM_API[row?.source] ?? row?.source ?? 'manual',
  message: row?.message ?? { zh: '', en: '' },
  linkText: row?.linkText ?? { zh: '', en: '' },
  linkUrl: row?.linkUrl ?? '',
  dismissible: row?.dismissible,
  startAt: row?.startAt ?? null,
  endAt: row?.endAt ?? null,
  venue: row?.venue,
  enabled: row?.enabled,
  createdAt: row?.createdAt,
  updatedAt: row?.updatedAt,
  hasAuditLog: row?.hasAuditLog,
});

export const toApiPayload = (data: Partial<MaintenanceBannerRecord>, isUpdate = false) => {
  const payload: Record<string, unknown> = {};

  if (data.code != null && String(data.code).trim()) payload.code = String(data.code).trim();
  if (data.scope) payload.scope = SCOPE_TO_API[data.scope as BannerScope] ?? data.scope;
  if (data.level) payload.level = LEVEL_TO_API[data.level as BannerLevel] ?? data.level;
  if (data.message) payload.message = normalizeLocalized(data.message) ?? { en: '', zh: '' };

  const linkText = normalizeLocalized(data.linkText);
  if (linkText) payload.linkText = linkText;
  else if (isUpdate) payload.linkText = null;

  if (data.linkUrl != null) {
    const url = String(data.linkUrl).trim();
    if (url) payload.linkUrl = url;
    else if (isUpdate) payload.linkUrl = null;
  }

  if (data.version != null) payload.version = data.version;
  if (data.enabled != null) payload.enabled = data.enabled;
  if (data.dismissible != null) payload.dismissible = data.dismissible;
  if (data.venue != null) payload.venue = data.venue.trim() ? data.venue.trim() : null;
  if (data.startAt != null) payload.startAt = data.startAt || null;
  if (data.endAt != null) payload.endAt = data.endAt || null;

  return payload;
};

export const filterToApiParams = (filter: Record<string, unknown> = {}) => {
  const params: Record<string, string> = {};
  if (filter.search) params.search = String(filter.search);
  if (filter.scope) params.scope = SCOPE_TO_API[filter.scope as BannerScope] ?? String(filter.scope);
  if (filter.level) params.level = LEVEL_TO_API[filter.level as BannerLevel] ?? String(filter.level);
  if (filter.source) params.source = SOURCE_TO_API[filter.source as BannerSource] ?? String(filter.source);
  return params;
};
